<template>
  <div class="ag-alert" :class="`ag-alert--${type}`">
    <div class="ag-alert__head">
      <div class="ag-alert__head-left">
        <AGIcon v-if="icon" class="ag-alert__type-icon" :icon="icon" />
      </div>

      <div class="ag-alert__head-middle" v-html="message" />

      <div class="ag-alert__head-right">
        <AGProgressCircular v-if="loading" indeterminate :color="type" />

        <AGButton v-if="linkText" class="ag-alert__link-button" type="link" @click="emitClickLink">
          {{ linkText }}
        </AGButton>

        <AGButton
          v-if="hasDetails"
          type="link"
          :right-icon="showDetails ? 'md:keyboard_arrow_up' : 'md:keyboard_arrow_down'"
          @click="onClickToggleDetails"
        >
          {{ toggleDetailsText }}
        </AGButton>

        <AGButtonIcon
          v-if="closable"
          class="ag-alert__close-button"
          icon="md:close"
          size="sm"
          @click="emitClickClose"
        />
      </div>
    </div>

    <div v-if="hasDetails" class="ag-alert__body" :class="`ag-alert__body--${showDetails ? 'open' : 'closed'}`">
      <slot />
    </div>
  </div>
</template>

<script>
import AGButton from '@/components/common/AGButton.vue';
import AGButtonIcon from '@/components/common/AGButtonIcon.vue';
import AGIcon from '@/components/common/AGIcon.vue';
import AGProgressCircular from '@/components/common/AGProgressCircular.vue';

export default {
  components: { AGButton, AGButtonIcon, AGIcon, AGProgressCircular },
  emits: ['click:link', 'click:close', 'click:toggle'],

  props: {
    message: {
      type: String,
      required: true,
    },

    type: {
      type: String,
      default: 'default',
      validator: (val) => ['info', 'success', 'warning', 'error', 'default'].includes(val),
    },

    loading: {
      type: Boolean,
      default: false,
    },

    closable: {
      type: Boolean,
      default: false,
    },

    linkText: {
      type: String,
      required: false,
    },

    onClickLink: {
      type: Function,
      default: () => {},
    },

    showDetailsText: {
      type: String,
      default: 'Exibir detalhes',
    },

    hideDetailsText: {
      type: String,
      default: 'Ocultar detalhes',
    },

    noTextDetails: {
      type: Boolean,
      default: false,
    },
  },

  data: () => ({
    showDetails: false,
  }),

  computed: {
    icon() {
      switch (this.type) {
        case 'info':
          return 'md:info';
        case 'success':
          return 'md:check_circle';
        case 'warning':
          return 'md:warning_amber';
        case 'error':
          return 'md:cancel';
        default:
          return 'md:description';
      }
    },

    toggleDetailsText() {
      if (this.noTextDetails) return '';
      return this.showDetails ? this.hideDetailsText : this.showDetailsText;
    },

    hasDetails() {
      return !!this.$slots.default;
    },
  },

  methods: {
    emitClickLink(e) {
      this.$emit('click:link', e);
    },

    emitClickClose(e) {
      this.$emit('click:close', e);
    },

    emitClickToggle(e) {
      this.$emit('click:toggle', e);
    },

    onClickToggleDetails(e) {
      this.showDetails = !this.showDetails;

      this.emitClickToggle(e);
    },
  },
};
</script>

<style lang="scss" scoped>
.ag-alert {
  width: 100%;

  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: flex-start;

  border-width: 1px;
  border-radius: 8px;
  border-style: solid;

  overflow: hidden;

  .ag-alert__head {
    width: 100%;
    padding: 16px;

    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 16px;

    .ag-alert__head-left {
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: flex-start;
      gap: 16px;
    }

    .ag-alert__head-right {
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: flex-end;
      gap: 16px;
    }
  }

  .ag-alert__head-middle {
    flex: 1;
  }

  &.ag-alert--default {
    background-color: white;
    border-color: $brand-color-primary-medium;
    color: $brand-color-primary-medium;

    .ag-alert__type-icon {
      color: $brand-color-primary-medium;
    }

    .ag-alert__body {
      border-color: $brand-color-primary-medium;
    }
  }

  &.ag-alert--info {
    background-color: $feedback-color-information-light;
    border-color: $feedback-color-information-medium;
    color: $feedback-color-information-medium;

    .ag-alert__type-icon {
      color: $feedback-color-information-medium;
    }

    .ag-alert__body {
      border-color: $feedback-color-information-medium;
    }
  }

  &.ag-alert--success {
    background-color: $feedback-color-success-light;
    border-color: $feedback-color-success-medium;
    color: $feedback-color-success-medium;

    .ag-alert__type-icon {
      color: $feedback-color-success-medium;
    }

    .ag-alert__body {
      border-color: $feedback-color-success-medium;
    }
  }

  &.ag-alert--warning {
    background-color: $feedback-color-attention-light;
    border-color: $feedback-color-attention-medium;
    color: $feedback-color-attention-dark;

    .ag-alert__type-icon {
      color: $feedback-color-attention-medium;
    }

    .ag-alert__body {
      border-color: $feedback-color-attention-medium;
    }
  }

  &.ag-alert--error {
    background-color: $feedback-color-error-light;
    border-color: $feedback-color-error-medium;
    color: $feedback-color-error-medium;

    .ag-alert__type-icon {
      color: $feedback-color-error-medium;
    }

    .ag-alert__body {
      border-color: $feedback-color-error-medium;
    }
  }

  .ag-alert__body {
    padding: 16px;
    transition: all 0.2s ease-in-out;
    -webkit-transition: all 0.2s ease-in-out;
    -moz-transition: all 0.2s ease-in-out;
    overflow: hidden;
    background-color: white;

    &.ag-alert__body--open {
      border-top: solid 1px;
      height: auto;

      * {
        opacity: 1;
      }
    }

    &.ag-alert__body--closed {
      height: 0;
      padding: 0 16px;

      * {
        opacity: 0;
      }
    }
  }
}
</style>
